import * as SecureStore from 'expo-secure-store';
import * as React from 'react';
import { Platform } from 'react-native';

import {
  clockNow,
  createSystemClockState,
  isClockState,
  jumpClockBy,
  setClockSpeed,
  setClockTime,
  systemNowMs,
  type ClockState,
} from '@sixam/shared/clock';

const STORAGE_KEY = 'sixam.dev-clock.v1';

type Listener = () => void;

async function readPersistedClock(): Promise<ClockState | null> {
  if (!__DEV__) return null;
  try {
    let value: string | null = null;
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') value = window.localStorage.getItem(STORAGE_KEY);
    } else {
      value = await SecureStore.getItemAsync(STORAGE_KEY);
    }
    if (!value) return null;
    const parsed: unknown = JSON.parse(value);
    return isClockState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

async function persistClock(state: ClockState): Promise<void> {
  if (!__DEV__) return;
  const value = JSON.stringify(state);
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, value);
    } else {
      await SecureStore.setItemAsync(STORAGE_KEY, value);
    }
  } catch {
    // Persistence must never prevent the development clock from working in memory.
  }
}

export class DevClockStore {
  private state: ClockState;
  private readonly listeners = new Set<Listener>();

  constructor(initialState = createSystemClockState()) {
    this.state = initialState;
  }

  getSnapshot = (): ClockState => this.state;

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  replace(state: ClockState, persist = true) {
    this.state = state;
    for (const listener of this.listeners) listener();
    if (persist) void persistClock(state);
  }

  setTime(value: Date) {
    this.replace(setClockTime(this.state, value));
  }

  jumpBy(durationMs: number) {
    this.replace(jumpClockBy(this.state, durationMs));
  }

  setSpeed(speed: number) {
    this.replace(setClockSpeed(this.state, speed));
  }

  reset() {
    this.replace(createSystemClockState());
  }
}

export const devClockStore = new DevClockStore();

export function getCurrentTime(): Date {
  return clockNow(devClockStore.getSnapshot());
}

export function useClockState(store = devClockStore): ClockState {
  return React.useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

export function useCurrentTime(store = devClockStore): Date {
  const state = useClockState(store);
  const [, refresh] = React.useReducer((value: number) => value + 1, 0);
  const intervalMs = Math.min(1000, Math.max(100, 1000 / state.speed));

  React.useEffect(() => {
    const interval = setInterval(refresh, intervalMs);
    return () => clearInterval(interval);
  }, [intervalMs]);

  return clockNow(state);
}

export function DevClockProvider({ children }: Readonly<React.PropsWithChildren>) {
  React.useEffect(() => {
    let active = true;
    void readPersistedClock().then((state) => {
      if (active && state) devClockStore.replace(state, false);
    });
    return () => {
      active = false;
    };
  }, []);

  return children;
}

export function currentDevTimeHeader(): string | null {
  if (!__DEV__) return null;
  const state = devClockStore.getSnapshot();
  return state.enabled ? clockNow(state, systemNowMs()).toISOString() : null;
}
